export type OnboardingBillingPreview = {
  nextTransactionAt: string
  nextChargeAmountCents: number
}

const ONBOARDING_BILLING_PREVIEW_KEY = 'epic:onboarding-billing-preview'
const ONBOARDING_BILLING_PREVIEW_TTL_MS = 15 * 60 * 1000

type StoredOnboardingBillingPreview = OnboardingBillingPreview & {
  writtenAt: number
}

const isOnboardingBillingPreview = (value: unknown): value is OnboardingBillingPreview => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<OnboardingBillingPreview>
  return (
    typeof candidate.nextTransactionAt === 'string' &&
    candidate.nextTransactionAt.length > 0 &&
    typeof candidate.nextChargeAmountCents === 'number' &&
    Number.isFinite(candidate.nextChargeAmountCents)
  )
}

export const writeOnboardingBillingPreview = (preview: OnboardingBillingPreview) => {
  if (typeof window === 'undefined') {
    return
  }

  const nextPreview: StoredOnboardingBillingPreview = {
    ...preview,
    writtenAt: Date.now(),
  }

  window.sessionStorage.setItem(ONBOARDING_BILLING_PREVIEW_KEY, JSON.stringify(nextPreview))
}

export const clearOnboardingBillingPreview = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.removeItem(ONBOARDING_BILLING_PREVIEW_KEY)
}

export const readOnboardingBillingPreview = (): OnboardingBillingPreview | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = window.sessionStorage.getItem(ONBOARDING_BILLING_PREVIEW_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') {
      clearOnboardingBillingPreview()
      return null
    }

    const candidate = parsed as Partial<StoredOnboardingBillingPreview>
    if (
      typeof candidate.writtenAt !== 'number' ||
      !Number.isFinite(candidate.writtenAt) ||
      Date.now() - candidate.writtenAt > ONBOARDING_BILLING_PREVIEW_TTL_MS
    ) {
      clearOnboardingBillingPreview()
      return null
    }

    return isOnboardingBillingPreview(candidate) ? candidate : null
  } catch {
    clearOnboardingBillingPreview()
    return null
  }
}

export const readAndClearOnboardingBillingPreview = (): OnboardingBillingPreview | null => {
  const preview = readOnboardingBillingPreview()
  clearOnboardingBillingPreview()
  return preview
}
